<template>
  <div
    class="container d-flex flex-column px-4"
    style="position: absolute; top: 50px"
  >
    <div
      class="mb-3"
      v-for="(question, index) in antiMoneyLaunderingSurvey?.questions"
      :key="index"
    >
      <div class="questionTitle">
        <span>
          {{ t(question.questionTitle) }}
        </span>
      </div>
      <div class="field">
        <SelectInputExpandable
          :placeholder="
            t(
              'PAYJOY_CARD_VIEWS.ANTI_MONEY_LAUNDERING_SURVEY.QUESTION_PLACEHOLDER'
            )
          "
          :options="translateOptions(question.options)"
          :fill="true"
          v-model:value="answers[index]"
          v-model:otherValue="otherAnswers[index]"
          id="anti-money-laundering-question"
          :error="questionFieldError"
        />
        <div
          v-if="
            question.haveExtraQuestions &&
            answers[index] === 'Yes' &&
            question.extraQuestionOptions
          "
        >
          <div
            v-for="(extra, extraIdx) in question.extraQuestionOptions"
            :key="`${index}-${extraIdx}`"
          >
            <SelectInputExpandable
              v-if="extra.type === 'select'"
              :placeholder="translatePlaceholder(extra.placeholder)"
              :options="translateOptions(extra.options)"
              :fill="true"
              v-model:value="extraAnswers[index][extraIdx]"
              v-model:otherValue="extraOtherAnswers[index][extraIdx]"
              id="anti-money-laundering-extra-question"
              :error="questionFieldError"
            />
            <TextInput
              v-else-if="extra.type === 'text'"
              :placeholder="translatePlaceholder(extra.placeholder)"
              :fill="true"
              v-model:value="extraAnswers[index][extraIdx]"
              id="anti-money-laundering-extra-text"
              :error="questionFieldError"
            />
            <div v-if="extra.type === 'calendar' && extra.extraTitle">
              <div class="questionTitle">
                <span>
                  {{ translatePlaceholder(extra.extraTitle) }}
                </span>
                <div class="datepickers-row">
                  <DatePicker
                    class="mt-0"
                    placeholder="Fecha de inicio"
                    :fill="true"
                    v-model:value="extraAnswers[index][extraIdx][0]"
                    :max-date="new Date()"
                  />
                  <DatePicker
                    class="mt-0"
                    placeholder="Fecha de cierre"
                    :fill="true"
                    v-model:value="extraAnswers[index][extraIdx][1]"
                    :max-date="new Date()"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="floating-btn" v-if="!disabledBtn">
      <Button
        id="btn-continue"
        class="text-start w-100"
        @click="onConfirmClick"
        :disabled="disabledBtn"
        :loading="confirmLoading"
      >
        <div class="text-white text-center my-auto fw-bold btn-text">
          <span>{{
            t("PAYJOY_CARD_VIEWS.ANTI_MONEY_LAUNDERING_SURVEY.BUTTON")
          }}</span>
        </div>
      </Button>
    </div>
  </div>
  <CompletePageModal
    v-if="showModal"
    :modalHeaderImage="yellowWarningIcon"
    :modalHeaderTitle="
      t('PAYJOY_CARD_VIEWS.ANTI_MONEY_LAUNDERING_SURVEY.ERROR_MODAL_TITLE')
    "
    :modalHeaderSubtitle="
      t(
        'PAYJOY_CARD_VIEWS.ANTI_MONEY_LAUNDERING_SURVEY.ERROR_MODAL_DESCRIPTION'
      )
    "
    :btnText="t('PAYJOY_CARD_VIEWS.ANTI_MONEY_LAUNDERING_SURVEY.BUTTON')"
    :showCloseButton="false"
    @modalAction="goHome"
  />
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  onMounted,
  getCurrentInstance,
  inject,
} from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Button from "@/apps/credit-line/components/Button.vue";
import SelectInputExpandable from "@/apps/credit-line/components/SelectInputExpandable.vue";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import TextInput from "@/apps/credit-line/components/TextInput.vue";
import DatePicker from "@/apps/credit-line/components/DatePicker.vue";
import { setLanguage } from "@/apps/credit-line/helpers/translations.helper";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { safeAndroidCall } from "@/apps/credit-line/helpers/safe-android-call.helper";
import CompletePageModal from "@/apps/credit-line/components/CompletePageModal.vue";

//Check the service that returns the anti-money laundering survey mapper with questions
const antiMoneyLaunderingSurvey = inject("amlSettings");
//Obtaining the answers from the mapper and initializing them
const answers = ref<string[]>(
  Array(antiMoneyLaunderingSurvey?.value?.questions?.length || 0).fill("")
);
const previousAnswers = ref([...answers.value]);
const questionFieldError = ref<boolean>(false);
const otherAnswers = ref<string[]>([]);
const extraOtherAnswers = ref<Array<Array<string>>>([]);
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const extraAnswers = ref<Array<Array<any>>>([]);
const showModal = ref<boolean>(false);
const yellowWarningIcon = computed(() => {
  return useAsset("img/credit-line/yellow_warning_icon.svg");
});

// Initialize arrays when data is available
const initializeExtraArrays = () => {
  if (antiMoneyLaunderingSurvey?.value?.questions) {
    otherAnswers.value = Array(
      antiMoneyLaunderingSurvey.value.questions.length
    ).fill("");
    extraOtherAnswers.value = antiMoneyLaunderingSurvey.value.questions.map(
      (q) =>
        q.extraQuestionOptions ? q.extraQuestionOptions.map(() => "") : []
    );
    extraAnswers.value = antiMoneyLaunderingSurvey.value.questions.map((q) =>
      q.extraQuestionOptions
        ? q.extraQuestionOptions.map((extra) =>
            extra.type === "calendar"
              ? ["", ""] //For calendar, we will have two dates
              : ""
          )
        : []
    );
  }
};

// Initialize on mount
initializeExtraArrays();

// Watch for changes in case data loads after component mount
watch(
  () => antiMoneyLaunderingSurvey?.value?.questions,
  () => {
    initializeExtraArrays();
    answers.value = Array(
      antiMoneyLaunderingSurvey?.value?.questions?.length || 0
    ).fill("");
    previousAnswers.value = [...answers.value];
  }
);

// Emits
const emit = defineEmits<{
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  (e: "onContinueClick", data: any): void;
}>();

const { t } = useI18n();
//Translations of the values we have from the questions mapper
const translatePlaceholder = (placeholder: string | undefined) => {
  return typeof placeholder === "string" && placeholder ? t(placeholder) : "";
};
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const translateOptions = (options: any[]) => {
  return Array.isArray(options)
    ? options.map((option) => ({
        ...option,
        text: t(option.text),
      }))
    : [];
};

const goHome = () => {
  safeAndroidCall("showNav");
  safeAndroidCall("dismissWebView");
};

const confirmLoading = ref<boolean>(false);

watch(
  answers,
  (newVal) => {
    newVal.forEach((answer, idx) => {
      if (answer !== "" && answer !== previousAnswers.value[idx]) {
        const question = antiMoneyLaunderingSurvey?.value?.questions[idx];
        const eventName = question?.analyticsEventName;
        if (
          eventName &&
          AnalyticEvents.CreditLine[
            eventName as keyof typeof AnalyticEvents.CreditLine
          ]
        ) {
          logAnalyticsEvent(
            AnalyticEvents.CreditLine[
              eventName as keyof typeof AnalyticEvents.CreditLine
            ],
            "Anti Money Laundering",
            answer
          );
        }
      }
    });
    previousAnswers.value = [...newVal];
  },
  { deep: true }
);

watch(
  otherAnswers,
  (newVal) => {
    if (newVal.some((answer) => answer !== "")) {
      questionFieldError.value = false;
    }
  },
  { deep: true }
);
const disabledBtn = computed(() => {
  // Validate if all main questions have an answer
  const mainRequired = answers.value.some((answer) => answer === "");
  //Validate if all extra questions have an answer
  const extraRequired = antiMoneyLaunderingSurvey?.value?.questions?.some(
    (q, idx) => {
      return (
        q.haveExtraQuestions &&
        answers.value[idx] === "Yes" &&
        q.extraQuestionOptions &&
        q.extraQuestionOptions.length > 0 &&
        q.extraQuestionOptions.some((extra, extraIdx) => {
          if (extra.type === "calendar") {
            return extraAnswers.value[idx][extraIdx].some((date) => !date);
          } else {
            return extraAnswers.value[idx][extraIdx] === "";
          }
        })
      );
    }
  );

  return mainRequired || extraRequired;
});

// Lifecycle
onMounted(() => {
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.AmlQuestionsViewScreen,
    "Anti Money Laundering",
    "AML Flow started"
  );

  setLanguage(getCurrentInstance()?.proxy);
});

function onConfirmClick() {
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.AmlSubmitClick,
    "Anti Money Laundering",
    "Submit"
  );
  if (!disabledBtn.value) {
    // Check if any selected answer has shouldBlockFlow
    const hasBlockingAnswer = antiMoneyLaunderingSurvey?.value?.questions.some(
      (q, idx) => {
        const selectedAnswer = answers.value[idx];
        const selectedOption = q.options?.find(
          (opt) => opt.key === selectedAnswer
        );
        return selectedOption?.shouldBlockFlow === true;
      }
    );

    // Validate if all main questions have an answer and add them to data
    const data = {
      surveyVersion: antiMoneyLaunderingSurvey?.value?.surveyVersion,
      questions: antiMoneyLaunderingSurvey?.value?.questions.map((q, idx) => ({
        name: q.name,
        question: q.question,
        answer:
          answers.value[idx] === "OTHER"
            ? otherAnswers.value[idx]
            : answers.value[idx] === "Yes" && q.extraQuestionOptions?.length > 0
            ? [
                answers.value[idx],
                ...q.extraQuestionOptions.map((extra, extraIdx) =>
                  extraAnswers.value[idx][extraIdx] === "OTHER"
                    ? extraOtherAnswers.value[idx][extraIdx]
                    : Array.isArray(extraAnswers.value[idx][extraIdx])
                    ? extraAnswers.value[idx][extraIdx].join(" - ")
                    : extraAnswers.value[idx][extraIdx]
                ),
              ].join(", ")
            : answers.value[idx],
        share: 0,
      })),
      hasBlockingAnswer: hasBlockingAnswer,
    };

    confirmLoading.value = true;
    emit("onContinueClick", data);

    // If there's a blocking answer, show modal after emitting the data
    if (hasBlockingAnswer) {
      showModal.value = true;
      confirmLoading.value = false;
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.questionTitle {
  color: $pj-color-black;
  font-weight: bold;
  font-size: 17px;
  line-height: 1;
  margin-bottom: 10px;
}
.floating-btn-placeholder,
.floating-btn {
  width: 100%;
}
.floating-btn {
  background-color: white;
  position: fixed;
  padding: 0px 20px;
  left: 0;
  bottom: 0;
  z-index: 200;
}
.datepickers-row {
  display: flex;
  gap: 16px;
}
.datepickers-row > * {
  flex: 1;
}
</style>
